import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Lock, Search, Filter, User, Calendar, FileText,
  CheckCircle, XCircle, Clock, ChevronRight, Scale
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  APPROVED: { label: 'Approuvee', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  REJECTED: { label: 'Rejetee', color: 'bg-red-100 text-red-800', icon: XCircle },
  EXECUTED: { label: 'Executee', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
};

const RELEASE_TYPES = [
  { value: 'END_OF_SENTENCE', label: 'Fin de peine' },
  { value: 'CONDITIONAL', label: 'Liberation conditionnelle' },
  { value: 'PRESIDENTIAL_PARDON', label: 'Grace presidentielle' },
  { value: 'ACQUITTAL', label: 'Acquittement' },
  { value: 'AMNESTY', label: 'Amnistie' },
  { value: 'BAIL', label: 'Liberte provisoire sous caution' },
  { value: 'TRANSFER', label: 'Transfert' },
];

const PrisonerReleasesPage = () => {
  const { language, user: currentUser } = useAuth();
  const [releases, setReleases] = useState([]);
  const [prisoners, setPrisoners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    prisoner_id: '',
    release_type: '',
    release_date: new Date().toISOString().split('T')[0],
    decision_reference: '',
    decision_authority: '',
    conditions: '',
    notes: ''
  });

  useEffect(() => {
    loadReleases();
    loadPrisoners();
  }, [search, statusFilter]);

  const loadReleases = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const response = await api.get('/prisoners/releases', { params });
      setReleases(response.data || []);
    } catch (error) {
      // Endpoint may not exist yet - show empty state
      setReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPrisoners = async () => {
    try {
      const response = await api.get('/prisoners', { params: { limit: 200 } });
      const data = Array.isArray(response.data) ? response.data : response.data?.items || [];
      setPrisoners(data.filter(p => p.status !== 'RELEASED'));
    } catch { /* ignore */ }
  };

  const handleCreateRelease = async (e) => {
    e.preventDefault();
    if (!formData.prisoner_id || !formData.release_type) {
      toast.error('Selectionnez un detenu et un type de liberation');
      return;
    }
    try {
      await api.post('/prisoners/releases', formData);
      toast.success('Demande de liberation enregistree');
      setShowCreateModal(false);
      setFormData({ prisoner_id: '', release_type: '', release_date: new Date().toISOString().split('T')[0], decision_reference: '', decision_authority: '', conditions: '', notes: '' });
      loadReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    }
  };

  const selectedPrisoner = prisoners.find(p => p.prisoner_id === formData.prisoner_id);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="prisoner-releases-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Lock className="w-6 h-6 text-primary" />
              {language === 'fr' ? 'Liberations' : 'Releases'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr' ? 'Gestion des liberations et des mises en liberte' : 'Manage prisoner releases'}
            </p>
          </div>
          {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'JUDGE' || currentUser?.role === 'MAGISTRATE' || currentUser?.role === 'JUGE_PAIX' || currentUser?.role === 'SUBSTITUT_PROCUREUR' || currentUser?.role === 'PROCUREUR_REPUBLIQUE') && (
            <Button onClick={() => setShowCreateModal(true)} data-testid="new-release-btn">
              <FileText className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouvelle Liberation' : 'New Release'}
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{releases.filter(r => r.status === 'PENDING').length}</p>
                <p className="text-xs text-muted-foreground">En attente</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{releases.filter(r => r.status === 'APPROVED').length}</p>
                <p className="text-xs text-muted-foreground">Approuvees</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <Scale className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{releases.filter(r => r.status === 'EXECUTED').length}</p>
                <p className="text-xs text-muted-foreground">Executees</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{releases.filter(r => r.status === 'REJECTED').length}</p>
                <p className="text-xs text-muted-foreground">Rejetees</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'fr' ? "Rechercher par nom de detenu..." : "Search by prisoner name..."}
              className="pl-10" data-testid="release-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="PENDING">En attente</SelectItem>
              <SelectItem value="APPROVED">Approuvees</SelectItem>
              <SelectItem value="EXECUTED">Executees</SelectItem>
              <SelectItem value="REJECTED">Rejetees</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Releases List */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {releases.length} {language === 'fr' ? 'liberation(s)' : 'release(s)'}
            </CardTitle>
          </CardHeader>
          <ScrollArea className="h-[calc(100vh-380px)]">
            <CardContent className="p-2 space-y-2">
              {releases.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground" data-testid="releases-empty-state">
                  <Lock className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium">{language === 'fr' ? 'Aucune liberation enregistree' : 'No releases recorded'}</p>
                  <p className="text-xs mt-1">
                    {language === 'fr' ? 'Les liberations apparaitront ici une fois creees par un magistrat' : 'Releases will appear here once created by a magistrate'}
                  </p>
                </div>
              ) : (
                releases.map((release, i) => {
                  const statusMeta = STATUS_META[release.status] || STATUS_META.PENDING;
                  const StatusIcon = statusMeta.icon;
                  return (
                    <div key={release.release_id || i} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/40 transition-colors" data-testid={`release-item-${i}`}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{release.prisoner_name || 'Detenu inconnu'}</p>
                        <p className="text-xs text-muted-foreground">{RELEASE_TYPES.find(t => t.value === release.release_type)?.label || release.release_type}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className={statusMeta.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusMeta.label}
                        </Badge>
                        {release.release_date && (
                          <p className="text-[10px] text-muted-foreground mt-1 flex items-center justify-end gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(release.release_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Create Release Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg" data-testid="create-release-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Nouvelle Liberation
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRelease} className="space-y-4">
              <div className="space-y-2">
                <Label>Detenu *</Label>
                <Select value={formData.prisoner_id} onValueChange={(v) => setFormData({...formData, prisoner_id: v})}>
                  <SelectTrigger data-testid="select-prisoner">
                    <SelectValue placeholder="Selectionner un detenu" />
                  </SelectTrigger>
                  <SelectContent>
                    {prisoners.map(p => (
                      <SelectItem key={p.prisoner_id} value={p.prisoner_id}>
                        {p.first_name} {p.last_name} - {p.unji}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPrisoner && (
                  <div className="text-xs p-2 bg-muted/50 rounded border">
                    <p><strong>UNJI:</strong> {selectedPrisoner.unji}</p>
                    <p><strong>Statut:</strong> {selectedPrisoner.status}</p>
                    <p><strong>Charges:</strong> {selectedPrisoner.charges?.join(', ') || 'N/A'}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Type de liberation *</Label>
                <Select value={formData.release_type} onValueChange={(v) => setFormData({...formData, release_type: v})}>
                  <SelectTrigger data-testid="select-release-type">
                    <SelectValue placeholder="Selectionner le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {RELEASE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date de liberation</Label>
                  <Input type="date" value={formData.release_date} onChange={(e) => setFormData({...formData, release_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Reference decision</Label>
                  <Input value={formData.decision_reference} onChange={(e) => setFormData({...formData, decision_reference: e.target.value})}
                    placeholder="Ex: Jugement RP 1234/KIN" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Autorite decisionnaire</Label>
                <Input value={formData.decision_authority} onChange={(e) => setFormData({...formData, decision_authority: e.target.value})}
                  placeholder="Ex: Tribunal de Grande Instance de Kinshasa" />
              </div>

              <div className="space-y-2">
                <Label>Conditions (si liberation conditionnelle)</Label>
                <Textarea value={formData.conditions} onChange={(e) => setFormData({...formData, conditions: e.target.value})}
                  placeholder="Conditions a respecter..." rows={2} />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Observations..." rows={2} />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-release-btn">Enregistrer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PrisonerReleasesPage;
